import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Tag } from 'antd';
import {
  dataGridDateSorter,
  dataGridTextSorter,
} from '../../../../../common/DataGrid/sorters/DataGridSorters.jsx';
import { useDataGridTextFilter } from '../../../../../common/DataGrid/filters/useDataGridTextFilter.jsx';
import { Spinner } from '../../../../../common/index.jsx';
import CollapseBlock from '../../../../../common/blocks/CollapseBlock/CollapseBlock.jsx';
import DataGrid from '../../../../../common/DataGrid/DataGrid.jsx';
import PropTypes from 'prop-types';
import { IconButton } from '../../../../../common/controls/Controls.jsx';
import {
  CreditCardOutlined,
  DislikeOutlined,
  LikeOutlined,
  RedoOutlined,
  TeamOutlined,
  FileSearchOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { routes } from '../../../../../../utils/helpers/ApiClient.jsx';
import { useDataGridDateRangeFilter } from '../../../../../common/DataGrid/filters/useDataGridDateRangeFilter.jsx';
import {
  ROLE_FRI_JOBS_ADMIN,
  ROLE_FRI_JOBS_EXTERNAL_PARTNER,
  ROLE_FRI_JOBS_STUDENT,
} from '../../../../../../utils/constants/roles.js';
import { FJ_STATUS } from '../../../../../../utils/constants/enums.js';

const FjJobsTable = ({
  jobsData,
  onDetailClick,
  onInterestedClick,
  onDeleteJobClick,
  onEditClick,
  onApproveClick,
  onArchiveClick,
  onDisapproveClick,
  fromPublic,
  filterPanel,
  role,
  selectedJobListOption,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Custom settings
  const customSettings = (job) => {
    return (
      <div style={{ display: 'flex' }}>
        {selectedJobListOption === 'archived' && !fromPublic && (
          <IconButton
            icon={<RedoOutlined />}
            tooltip={t('Obnoviť')}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`${routes.cmsFriJobsJobEdit}/${job.id}?getFromArchive`);
            }}
          />
        )}
        <IconButton
          icon={<FileSearchOutlined />}
          tooltip={t('Zobraziť detail pracovnej ponuky')}
          onClick={(e) => {
            e.stopPropagation();
            onDetailClick(job.id);
          }}
        />
        {selectedJobListOption === 'waiting' &&
          !fromPublic &&
          role === ROLE_FRI_JOBS_ADMIN && (
            <>
              <IconButton
                icon={<LikeOutlined />}
                tooltip={t('Schváliť')}
                onClick={(e) => {
                  e.stopPropagation();
                  onApproveClick(job);
                }}
              />
              <IconButton
                icon={<DislikeOutlined />}
                tooltip={t('Neschváliť')}
                onClick={(e) => {
                  e.stopPropagation();
                  onDisapproveClick(job);
                }}
              />
            </>
          )}
        {role === ROLE_FRI_JOBS_STUDENT &&
          !job.already_applied &&
          !fromPublic && (
            <IconButton
              icon={<UserAddOutlined />}
              tooltip={t('Uchádzať sa o pracovnú ponuku')}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`${routes.cmsFriJobsInterestCreate}/${job.id}`);
              }}
            />
          )}

        {selectedJobListOption !== 'waiting' &&
          !fromPublic &&
          (role === ROLE_FRI_JOBS_ADMIN ||
            role === ROLE_FRI_JOBS_EXTERNAL_PARTNER) && (
            <IconButton
              icon={<TeamOutlined />}
              tooltip={t('Zobraziť zoznam záujemcov')}
              disabled={job.interested === 0}
              disabledTooltip={t('Nie sú žiadni záujemcovia')}
              onClick={(e) => {
                e.stopPropagation();
                onInterestedClick(job.id);
              }}
            />
          )}
        {selectedJobListOption === 'approved' &&
          !fromPublic &&
          (role === ROLE_FRI_JOBS_ADMIN ||
            role === ROLE_FRI_JOBS_EXTERNAL_PARTNER) && (
            <IconButton
              icon={<CreditCardOutlined />}
              tooltip={t('Archivovať')}
              onClick={(e) => {
                e.stopPropagation();
                onArchiveClick(job);
              }}
            />
          )}
      </div>
    );
  };

  // Sorters
  const partnerNameTextFilterProps = useDataGridTextFilter('partner_name');
  const jobTitleTextFilterProps = useDataGridTextFilter('title');
  const click_countTextFilterProps = useDataGridTextFilter('click_count');
  const interestedTextFilterProps = useDataGridTextFilter('interested');
  const capacityTextFilterProps = useDataGridTextFilter('capacity');
  const archived_atDateFilterProps = useDataGridDateRangeFilter('archived_at');
  const date_fromDateFilterProps = useDataGridDateRangeFilter('date_from');
  const date_toDateFilterProps = useDataGridDateRangeFilter('date_to');
  const approvedTextFilterProps = useDataGridTextFilter('approved');
  const salaryTextFilterProps = useDataGridTextFilter('salary');
  const placeOfWorkTextFilterProps = useDataGridTextFilter('place_of_work');

  // Tabulka pre Public
  const dataGridColumnsPublic = useMemo(
    () => [
      {
        title: t('Externý partner FRI'),
        dataIndex: 'partner_name',
        sorter: dataGridTextSorter('partner_name'),
        ...partnerNameTextFilterProps,
      },
      {
        title: t('Názov pracovnej ponuky'),
        dataIndex: 'title',
        width: '34%',
        sorter: dataGridTextSorter('title'),
        ...jobTitleTextFilterProps,
      },
      {
        title: t('Top'),
        dataIndex: 'top',
        render: (top) => {
          return top ? <Tag color="warning">TOP</Tag> : '-';
        },
      },
      // Show capacity, salary and place_of_work for students
      ...(role === ROLE_FRI_JOBS_STUDENT
        ? [
            {
              title: t('Pozície'),
              dataIndex: 'capacity',
              sorter: dataGridTextSorter('capacity'),
              ...capacityTextFilterProps,
            },
            {
              title: t('Plat'),
              dataIndex: 'salary',
              sorter: dataGridTextSorter('salary'),
              ...salaryTextFilterProps,
            },
            {
              title: t('Miesto výkonu práce'),
              dataIndex: 'place_of_work',
              sorter: dataGridTextSorter('place_of_work'),
              ...placeOfWorkTextFilterProps,
            },
          ]
        : [
            // keep original capacity column for non-students as before
            {
              title: t('Pozície'),
              dataIndex: 'capacity',
              sorter: dataGridTextSorter('capacity'),
              ...capacityTextFilterProps,
            },
          ]),
      {
        title: t('Záujemcovia'),
        dataIndex: 'interested',
        sorter: dataGridTextSorter('interested'),
        ...interestedTextFilterProps,
      },
      {
        title: t('Zobrazenia'),
        dataIndex: 'click_count',
        sorter: dataGridTextSorter('click_count'),
        ...click_countTextFilterProps,
      },
    ],
    [t, role],
  );

  // map function for approve with enum FJ_STATUS
  const status = (row) => {
    if (row.archived_at) {
      // Rejected: never approved and archived by admin
      if (!row.approved_at) {
        return t(FJ_STATUS.REFUSED);
      }
      // Archived: previously approved and later archived
      return t(FJ_STATUS.ARCHIVED);
    }
    if (row.approved_at) {
      return t(FJ_STATUS.APPROVED);
    }
    return t(FJ_STATUS.WAITING_FOR_APPROVAL);
  };

  const dataGridColumnsCMS = useMemo(
    () => [
      ...(role !== ROLE_FRI_JOBS_EXTERNAL_PARTNER
        ? [
            {
              title: t('Externý partner FRI'),
              dataIndex: 'partner_name',
              sorter: dataGridTextSorter('partner_name'),
              ...partnerNameTextFilterProps,
            },
          ]
        : []),
      {
        title: t('Názov pracovnej ponuky'),
        dataIndex: 'title',
        width: '22%',
        sorter: dataGridTextSorter('title'),
      },
      {
        title: t('Top'),
        dataIndex: 'top',
        render: (top) => {
          return top ? <Tag color="warning">TOP</Tag> : '-';
        },
      },
      // For students show capacity, salary and place_of_work in CMS too
      ...(role === ROLE_FRI_JOBS_STUDENT
        ? [
            {
              title: t('Pozície'),
              dataIndex: 'capacity',
              sorter: dataGridTextSorter('capacity'),
              ...capacityTextFilterProps,
            },
            {
              title: t('Plat'),
              dataIndex: 'salary',
              sorter: dataGridTextSorter('salary'),
              ...salaryTextFilterProps,
            },
            {
              title: t('Miesto výkonu práce'),
              dataIndex: 'place_of_work',
              sorter: dataGridTextSorter('place_of_work'),
              ...placeOfWorkTextFilterProps,
            },
            {
              title: t('Nástup'),
              dataIndex: 'date_of_start',
              sorter: dataGridTextSorter('date_of_start'),
            },
          ]
        : []),
      // Show dates only for admin or external partner roles
      ...(role === ROLE_FRI_JOBS_ADMIN ||
      role === ROLE_FRI_JOBS_EXTERNAL_PARTNER
        ? [
            {
              title: t('Platná od'),
              dataIndex: 'date_from',
              sorter: dataGridDateSorter('date_from'),
              render: (date) =>
                (date && new Date(date).toLocaleDateString()) || '-',
              ...date_fromDateFilterProps,
            },
            {
              title: t('Platná do'),
              dataIndex: 'date_to',
              sorter: dataGridDateSorter('date_to'),
              render: (date) =>
                (date && new Date(date).toLocaleDateString()) || '-',
              ...date_toDateFilterProps,
            },
          ]
        : []),
      ...(selectedJobListOption !== 'waiting'
        ? [
            {
              title: t('Záujemcovia'),
              dataIndex: 'interested',
              sorter: dataGridTextSorter('interested'),
              ...interestedTextFilterProps,
            },
            {
              title: t('Zobrazenia'),
              dataIndex: 'click_count',
              sorter: dataGridTextSorter('click_count'),
              ...click_countTextFilterProps,
            },
          ]
        : []),
      ...(selectedJobListOption === 'archived'
        ? [
            {
              title: t('Archivovaná'),
              dataIndex: 'archived_at',
              sorter: dataGridDateSorter('archived_at'),
              render: (date) =>
                (date && new Date(date).toLocaleDateString()) || '-',
              ...archived_atDateFilterProps,
            },
          ]
        : []),
      // Show status only when filter is active (filterPanel is true)
      ...(filterPanel
        ? [
            {
              title: t('Stav'),
              dataIndex: 'approved',
              sorter: dataGridTextSorter('approved'),
              render: (_, row) => (
                <Tag
                  color={
                    status(row) === FJ_STATUS.APPROVED
                      ? 'green'
                      : status(row) === FJ_STATUS.WAITING_FOR_APPROVAL
                        ? 'orange'
                        : status(row) === FJ_STATUS.ARCHIVED
                          ? 'blue'
                          : 'red'
                  }
                >
                  {status(row)}
                </Tag>
              ),
              ...approvedTextFilterProps,
            },
          ]
        : []),
    ],
    [t, selectedJobListOption, filterPanel, role],
  );

  return !jobsData ? (
    <Spinner />
  ) : (
    <>
      <div>
        <CollapseBlock
          title={
            filterPanel
              ? t(
                  selectedJobListOption === 'approved'
                    ? 'Zoznam schválených pracovných ponúk'
                    : selectedJobListOption === 'waiting'
                      ? 'Zoznam pracovných ponúk čakajúcich na schválenie'
                      : 'Zoznam archivovaných pracovných ponúk',
                )
              : null
          }
        >
          <DataGrid
            customSettings={customSettings}
            optionsWidth={fromPublic ? 110 : 200}
            rowKey={(job) => job.id}
            columns={fromPublic ? dataGridColumnsPublic : dataGridColumnsCMS}
            dataSource={jobsData || []}
            permissionsBasic={'fri-jobs-jobs'}
            uniqueTableId={'fri-jobs-jobs-table'}
            onDelete={onDeleteJobClick}
            onEditClick={
              selectedJobListOption !== 'archived' ? onEditClick : undefined
            }
            onRowClick={
              selectedJobListOption !== 'archived' ? onEditClick : undefined
            }
            onRowDetailClick={(job) => onDetailClick(job.id)}
          />
        </CollapseBlock>
      </div>
    </>
  );
};

FjJobsTable.propTypes = {
  user: PropTypes.any,
  jobsData: PropTypes.any,
  onDetailClick: PropTypes.func,
  onInterestedClick: PropTypes.func,
  onDeleteJobClick: PropTypes.func,
  onEditClick: PropTypes.func,
  onApproveClick: PropTypes.func,
  onArchiveClick: PropTypes.func,
  onDisapproveClick: PropTypes.func,
  onDeleteInterestClick: PropTypes.func,
  fromPublic: PropTypes.bool,
  filterPanel: PropTypes.bool,
  role: PropTypes.string,
  selectedJobListOption: PropTypes.string,
};

export default FjJobsTable;
